import { Component, OnInit } from "@angular/core";

import { environment } from "../../../environments/environment";
import { SalesStoreService } from "./services/sales-store.service";
import { SalesService } from "./services/sales.service";
import { LoaderService } from "../../loader/loader.service";
import { Subject } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntil } from "rxjs/operators";
import { PermissionsService } from "../../services/permissions.service";
import * as moment from "moment";
import { DateRangeModel } from "../../components/datepicker-range/datepicker-i18n.service";
import { Sale } from "./models/sale";
import { SaleRecord } from "./models/sale-record";
import { IDropdownSettings } from "ng-multiselect-dropdown";
import { SALE_ORIGIN_XELT } from "./models/sale-constants";
import { TeamsService } from "../../teams/teams.service";
import { UsersService } from "../../users/users.service";
import { Equipe } from "../../teams/models/equipe";
import { EquipeFind } from "../../teams/models/equipe-find";
import { User } from "../../users/models/user";

@Component({
  selector: "app-sales",
  templateUrl: "./sales.component.html",
  styleUrls: ["./sales.component.scss"],
})
export class SalesComponent implements OnInit {
  // Teto de vendas por requisicao, espelha MAX_SALE_RECORDS_BATCH do backend-virtus.
  static readonly SIGNATURE_BATCH_SIZE = 500;

  env;
  activeTab: string = "sync";

  fromDate: Date;
  toDate: Date;

  fromDateState: Date;
  toDateState: Date;

  teamsDropdownList: Equipe[] = [];
  teamsDropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: "_id",
    textField: "nome",
    selectAllText: "Selecionar Todos",
    unSelectAllText: "Limpar Seleção",
    itemsShowLimit: 1,
    allowSearchFilter: true,
    searchPlaceholderText: "Pesquisar",
    noDataAvailablePlaceholderText: "Sem dados disponíveis",
  };
  selectedTeams = [];

  sellersDropdownList: User[] = [];
  sellersDropdownSettings: IDropdownSettings = {
    ...this.teamsDropdownSettings,
    idField: "codigo_externo",
  };
  selectedSellers = [];

  teams: Equipe[] = [];
  sellers: User[] = [];

  // Descarta respostas de buscas antigas quando o filtro muda antes da anterior terminar.
  private salesRequestId = 0;
  // Idem para as listas de equipes/vendedores ao trocar de empresa.
  private dropdownsRequestId = 0;

  private unsubscribe$ = new Subject();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private salesStore: SalesStoreService,
    private salesService: SalesService,
    private loaderService: LoaderService,
    private teamsService: TeamsService,
    private usersService: UsersService,
    public permissionsService: PermissionsService
  ) {
    this.env = environment;
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(async params => {
      if (this.permissionsService.isPermitted("vendas")) {
        this.getActiveTabState();
        this.getDateRangeState();
        this.getFiltersState();

        // Falha nas listas nao pode impedir a busca de vendas.
        const dropdowns = this.setDropdowns().catch(err => console.error("Falha ao carregar equipes/vendedores", err));
        // Filtro salvo pode ser de outra empresa: so busca depois de validar contra as listas.
        if (this.selectedTeams.length || this.selectedSellers.length) {
          await dropdowns;
        }
        this.refreshSales();
      } else {
        this.router.navigateByUrl(`/dash/${params.cnpj}`);
      }
    });
  }

  async getSales(cnpj: string, fromDate: Date, toDate: Date) {
    const requestId = ++this.salesRequestId;
    const loader = await this.loaderService.notifica({
      title: "Buscando vendas...",
      date: moment().toISOString(),
      type: "loading",
    });

    let supervisor = null;
    if (this.permissionsService.isProfile("Supervisor")) {
      supervisor = this.env.user._id;
    }

    const teams = this.selectedTeams.map(team => team._id);
    const sellers = this.selectedSellers.map(seller => seller.codigo_externo);

    const sales = await this.salesService.getSales(cnpj, fromDate, toDate, supervisor, teams, sellers).toPromise();
    if (requestId !== this.salesRequestId) {
      loader.dismiss();
      return;
    }

    await this.fetchSignatureStatuses(sales);
    if (requestId === this.salesRequestId) {
      this.salesStore.addSales(sales);
    }
    loader.dismiss();
  }

  // O statusAssinatura so e consumido pelas abas "Aguardando Aprovacoes" (status aguardando)
  // e "Aguardando Cliente" (origem xelt). Buscar o registro das demais vendas gera uma
  // enxurrada de requisicoes sem uso.
  private needsSignatureStatus(sale: Sale): boolean {
    return (sale.status && sale.status.toLowerCase() === "aguardando") || sale.origin === SALE_ORIGIN_XELT;
  }

  private async fetchSignatureStatuses(sales: Sale[]) {
    const pending = sales.filter(sale => this.needsSignatureStatus(sale));
    if (!pending.length) {
      return;
    }

    const recordBySaleId = new Map<string, SaleRecord>();

    for (let i = 0; i < pending.length; i += SalesComponent.SIGNATURE_BATCH_SIZE) {
      const batch = pending.slice(i, i + SalesComponent.SIGNATURE_BATCH_SIZE);

      const response = await this.salesService
        .getSaleRecords(batch.map(sale => sale._id))
        .toPromise()
        .catch(() => null);

      const records = (response && response.records) || [];
      records.forEach(record => recordBySaleId.set(record.saleId, record));
    }

    // Sem registro = venda sem contrato eletronico (o backend nao devolve entrada
    // para ela), que as abas tratam como "sem contrato".
    pending.forEach(sale => {
      sale.statusAssinatura = recordBySaleId.get(sale._id) || null;
    });
  }

  refreshSales() {
    if (this.fromDate && this.toDate) {
      this.getSales(this.env.selected.cnpj, this.fromDate, this.toDate);
    }
  }

  getDateRangeState() {
    this.fromDate = this.salesStore.fromDate;
    this.toDate = this.salesStore.toDate;
  }

  onDateRangeChange(dateRange: DateRangeModel) {
    this.fromDate = dateRange.fromDate;
    this.toDate = dateRange.toDate;

    this.salesStore.fromDate = this.fromDate;
    this.salesStore.toDate = this.toDate;

    this.refreshSales();
  }

  getFiltersState() {
    this.selectedTeams = this.salesStore.selectedTeams;
    this.selectedSellers = this.salesStore.selectedSellers;
  }

  async setDropdowns() {
    const requestId = ++this.dropdownsRequestId;
    const [teams, sellers] = await Promise.all([this.getTeams(), this.getSellers()]);
    if (requestId !== this.dropdownsRequestId) {
      return;
    }

    this.teams = teams;
    this.sellers = sellers;
    this.teamsDropdownList = teams;

    this.selectedTeams = this.selectedTeams.filter(selected => teams.some(team => team._id === selected._id));
    this.updateSellersDropdown();
    this.saveFiltersState();
  }

  async getTeams(): Promise<Equipe[]> {
    // Supervisor ja e limitado as proprias equipes na consulta, igual ao dashboard.
    if (this.permissionsService.isProfile("Supervisor")) {
      return [];
    }

    const teams = await this.teamsService.getTeams().toPromise();
    return Array.isArray(teams) ? teams : [];
  }

  async getSellers(): Promise<User[]> {
    const users = await this.usersService.getUsers(this.env.selected.cnpj).toPromise();
    if (!Array.isArray(users)) {
      return [];
    }

    const sellers = users.filter(
      user => user.codigo_externo && user.novoPerfil && (user.novoPerfil.nome || "").includes("Vendedor")
    );

    if (!this.permissionsService.isProfile("Supervisor")) {
      return sellers;
    }

    const supervisorTeams: EquipeFind[] = await this.teamsService.getUserTeams(this.env.user._id).toPromise();
    const supervisorUsers = (Array.isArray(supervisorTeams) ? supervisorTeams : []).reduce(
      (teamUsers, team) => [...teamUsers, ...(team.usuarios || [])],
      []
    );

    return sellers.filter(seller => supervisorUsers.some(user => user._id === seller._id));
  }

  // Com equipes selecionadas, lista so os vendedores membros delas e tira da selecao quem nao pertence.
  updateSellersDropdown() {
    if (this.selectedTeams.length) {
      const members = this.teams
        .filter(team => this.selectedTeams.some(selected => selected._id === team._id))
        .reduce((teamMembers, team) => [...teamMembers, ...(team.membros || [])], []);

      this.sellersDropdownList = this.sellers.filter(seller => members.some(member => member._id === seller._id));
    } else {
      this.sellersDropdownList = this.sellers;
    }

    this.selectedSellers = this.selectedSellers.filter(selected =>
      this.sellersDropdownList.some(seller => seller.codigo_externo === selected.codigo_externo)
    );
  }

  onChangeTeams(teams: any[]) {
    this.selectedTeams = teams;
    this.updateSellersDropdown();
    this.saveFiltersState();
    this.refreshSales();
  }

  onChangeSellers(sellers: any[]) {
    this.selectedSellers = sellers;
    this.saveFiltersState();
    this.refreshSales();
  }

  saveFiltersState() {
    this.salesStore.selectedTeams = this.selectedTeams;
    this.salesStore.selectedSellers = this.selectedSellers;
  }

  getActiveTabState() {
    this.activeTab = this.salesStore.activeTab;
  }

  onChangeActiveTab(tab: string) {
    this.activeTab = tab;
    this.salesStore.activeTab = this.activeTab;
  }

  goBack(): void {
    this.router.navigateByUrl(`/salesmodule/${this.env.selected.cnpj}`);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
